import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../App';
import { FaBell, FaCheck, FaTrash, FaTimes } from 'react-icons/fa';
import { toast } from 'sonner';

export default function NotificationBell() {
  const { i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const navigate = useNavigate();
  
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    
    try {
      const [notifRes, countRes] = await Promise.all([
        axios.get(`${API}/notifications?user_id=${user.id}`),
        axios.get(`${API}/notifications/count?user_id=${user.id}`)
      ]);
      
      setNotifications(notifRes.data);
      setUnreadCount(countRes.data.count);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, [API, user]);

  // Initial fetch and polling every 30 seconds
  useEffect(() => {
    if (user) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user, fetchNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`);
      fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/notifications/mark-all-read?user_id=${user.id}`);
      toast.success(i18n.language === 'fr' ? 'Toutes les notifications marquées comme lues' : 'All notifications marked as read');
      fetchNotifications();
    } catch (error) {
      console.error('Error marking all as read:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur' : 'Error');
    } finally {
      setLoading(false);
    }
  };

  const deleteNotification = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/notifications/${notificationId}`);
      fetchNotifications();
      toast.success(i18n.language === 'fr' ? 'Notification supprimée' : 'Notification deleted');
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    
    if (notification.link) {
      navigate(notification.link);
      setShowDropdown(false);
    }
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return i18n.language === 'fr' ? "À l'instant" : 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} ${i18n.language === 'fr' ? 'min' : 'min'}`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} ${i18n.language === 'fr' ? 'h' : 'h'}`;
    return `${Math.floor(seconds / 86400)} ${i18n.language === 'fr' ? 'j' : 'd'}`;
  };

  if (!user) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon */}
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 text-gray-600 hover:text-[#0A2540] transition-colors"
        data-testid="notification-bell"
      >
        <FaBell size={20} />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-2xl border border-gray-200 z-50 max-h-[500px] overflow-y-auto">
          {/* Header */}
          <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
            <h3 className="font-bold text-lg text-[#0A2540]">
              {i18n.language === 'fr' ? 'Notifications' : 'Notifications'}
            </h3>
            <div className="flex gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  disabled={loading}
                  className="text-sm text-[#635BFF] hover:underline flex items-center gap-1"
                >
                  <FaCheck size={12} />
                  {i18n.language === 'fr' ? 'Tout lire' : 'Mark all read'}
                </button>
              )}
              <button
                onClick={() => setShowDropdown(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimes size={16} />
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="divide-y divide-gray-100">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <FaBell size={40} className="mx-auto mb-3 text-gray-300" />
                <p>{i18n.language === 'fr' ? 'Aucune notification' : 'No notifications'}</p>
              </div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  onClick={() => handleNotificationClick(notif)}
                  className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                    !notif.read ? 'bg-blue-50' : ''
                  }`}
                  data-testid={`notification-${notif.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className={`font-semibold text-sm ${!notif.read ? 'text-[#0A2540]' : 'text-gray-800'}`}>
                          {notif.title}
                        </h4>
                        {!notif.read && (
                          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-1">{notif.message}</p>
                      <span className="text-xs text-gray-400">{getTimeAgo(notif.created_at)}</span>
                    </div>
                    <button
                      onClick={(e) => deleteNotification(notif.id, e)}
                      className="ml-2 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <FaTrash size={12} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
